// Sentry init — must be the very first import in index.js so error boundaries,
// fetch instrumentation, and unhandled-rejection handlers are attached before
// any app code runs. SDK no-ops cleanly when REACT_APP_SENTRY_DSN is empty,
// so this file is safe to ship in every environment regardless of whether a
// project has been provisioned in sentry.io yet.
//
// Skill used: https://github.com/getsentry/sentry-for-ai/blob/main/skills/sentry-react-sdk/SKILL.md
import * as Sentry from '@sentry/react';

const dsn = process.env.REACT_APP_SENTRY_DSN;
// REACT_APP_ENV is dresez's existing build-time switch: 'PROD' on prod
// builds (set in frontend/.env.production), 'DEV' or absent otherwise.
// Mapped to lowercase so the Sentry dashboard's environment filter reads
// consistently.
const environment = (process.env.REACT_APP_ENV || 'dev').toLowerCase();

Sentry.init({
  dsn,
  environment,

  integrations: [
    // Auto-capture page loads + SPA navigations as transactions. Works
    // without a router-specific integration; for React Router v5 the
    // generic browserTracingIntegration listens to the History API
    // directly, which is good enough for the route-transition view.
    Sentry.browserTracingIntegration(),

    // Session Replay. Mask all text + block all media because dresez is
    // a marketplace — chat messages, item descriptions, profile fields,
    // and the photos themselves are all PII. We still get a visual
    // recording of which buttons were tapped and how the layout
    // rendered, just without the user-typed content.
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Performance traces: keep on, sample lightly in prod to stay inside
  // the free-tier quota (the dashboard surfaces page-load timings,
  // network calls per route, and rendering hot-spots). 100% in dev so
  // every local test session is fully observable.
  tracesSampleRate: environment === 'prod' ? 0.1 : 1.0,

  // Propagate Sentry's tracing headers (sentry-trace, baggage) on
  // requests to our backend. Without this, distributed traces stop at
  // the browser → api boundary and we can't tie a slow chat message
  // back to its server-side root cause.
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.dresez\.com/,
  ],

  // Replays: never sample baseline traffic (lots of innocuous sessions),
  // always replay on error so the failure that surfaced an issue comes
  // with a visual trace.
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,

  // PII guard: do NOT auto-attach IPs / browser headers / user emails.
  // Marketplace context — we can attach userId selectively via
  // Sentry.setUser({ id }) where appropriate; default off keeps us
  // privacy-clean by default.
  sendDefaultPii: false,
});
